import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { fmtMoney, fmtNum, imgSrc } from "../lib/api";
import { STATUS_STYLES } from "../lib/constants";

export const StatusBadge = ({ status, testid }) => (
  <span className={`inline-block border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${STATUS_STYLES[status] || "bg-white text-navy border-navy/20"}`} data-testid={testid}>
    {status}
  </span>
);

const Stat = ({ label, value }) =>
  value ? (
    <div>
      <dt className="text-[10px] uppercase tracking-[0.14em] text-mist">{label}</dt>
      <dd className="metric text-sm mt-0.5">{value}</dd>
    </div>
  ) : null;

export const PropertyCard = ({ p, match }) => {
  const price = p.asking_price || p.est_acquisition_price;
  return (
    <Link
      to={`/opportunities/${p.slug}`}
      className="group block bg-white border border-navy/10 hover:border-navy/30 transition-[border-color,transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_24px_48px_-24px_rgba(11,23,38,0.35)]"
      data-testid={`property-card-${p.slug}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-navy">
        <img src={imgSrc(p.images?.[0])} alt={`${p.property_type} property in ${p.city}, Oregon`} loading="lazy" className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          <StatusBadge status={p.status} />
          {p.category === "distressed" && <span className="bg-navy text-paper px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]">{p.distress_type || "Distressed"}</span>}
        </div>
        {p.is_sample && <span className="absolute bottom-3 left-3 bg-paper/90 text-navy px-2 py-0.5 font-mono text-[10px] tracking-widest" data-testid="sample-badge">SAMPLE LISTING</span>}
        {match && <span className="absolute top-3 right-3 bg-gold text-navy px-2.5 py-1 font-mono text-xs font-semibold" data-testid={`match-score-${p.slug}`}>{match.score}% Match</span>}
      </div>
      <div className="p-6">
        <p className="eyebrow !text-mist">{p.property_type}</p>
        <h3 className="mt-2 text-2xl font-semibold text-navy leading-tight">{p.title}</h3>
        <p className="mt-1.5 flex items-center gap-1.5 text-sm text-mist"><MapPin className="h-3.5 w-3.5" />{p.city}, Oregon</p>
        {price && <p className="metric mt-4 text-xl" data-testid={`property-card-price-${p.slug}`}>{fmtMoney(price)}{!p.asking_price && <span className="ml-2 font-sans text-[10px] uppercase tracking-wider text-mist">Est. acquisition</span>}</p>}
        <dl className="mt-4 grid grid-cols-3 gap-3 border-t border-navy/10 pt-4">
          <Stat label="Size" value={fmtNum(p.sqft, " SF") || p.lot_size} />
          <Stat label="Units" value={fmtNum(p.units)} />
          <Stat label="Cap Rate" value={p.cap_rate ? `${p.cap_rate}%` : null} />
          <Stat label="NOI" value={fmtMoney(p.noi)} />
          <Stat label="Occupancy" value={p.occupancy != null ? `${p.occupancy}%` : null} />
        </dl>
        {match && <p className="mt-4 text-xs text-navy/70 leading-relaxed">{match.summary}</p>}
        <div className="mt-5 flex items-center justify-between">
          <span className="text-xs text-mist">{(p.strategies || []).slice(0, 2).join(" · ")}</span>
          <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-navy group-hover:text-gold transition-colors" data-testid={`property-card-cta-${p.slug}`}>
            Request Deal Information <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </Link>
  );
};
